/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Button, Loading } from 'carbon-components-react';
import _ from 'lodash';
import React, { useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { ReadOnlyNotification } from '@ibm-watson/shared/modules/ia/components/access-control/ReadOnlyNotification';
import { EnvironmentTag } from '@ibm-watson/shared/modules/ia/components/environment-tag/EnvironmentTag';
import Panel from '@ibm-watson/shared/modules/ia/components/panel/Panel';
import {
  isAssistantReadOnly,
  isEnvironmentReadOnly,
  isSkillReadOnly
} from '@ibm-watson/shared/modules/ia/store/access-control/selectors';
import { updateAssistant } from '@ibm-watson/shared/modules/ia/store/assistants/actions';
import {
  selectIsSkillAutocreated,
  selectIsSkillEnabledInEnvironment,
  selectSkillReferencesForEnvironment
} from '@ibm-watson/shared/modules/ia/store/environments/selectors';
import {
  createSkill,
  listSkills,
  updateSkill
} from '@ibm-watson/shared/modules/ia/store/skills/actions';
import { selectSkillById } from '@ibm-watson/shared/modules/ia/store/skills/core-selectors';
import {
  selectIsUpdatingSkill,
  selectIsCreatingSkill,
  selectAreSkillsLoading
} from '@ibm-watson/shared/modules/ia/store/skills/selectors';
import {
  ctaEvents,
  pageEvents
} from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { Noop } from '../../../../ts';
import { FeatureFlag } from '../../../../ts/feature-flags';
import { selectAssistant } from '../../../ia/store/assistants/core-selectors';
import { updateEnvironment } from '../../../ia/store/environments/actions';
import {
  isFeatureEnabled,
  selectIsInsightActionsEnabled
} from '../../../ia/store/features/selectors';
import { useDiscoveryDraft } from '../../hooks/useDiscoveryDraft';
import {
  clearElasticDetails,
  selectInstanceId,
  selectProjectId,
  setIsChangingProject,
  setSearchSkillDraft,
  setShowDetailConfig
} from '../../store/reducer';
import {
  isShowDetailConfig,
  selectAreProjectsLoading,
  selectDraftSearchSkill,
  selectIsChangingProject,
  selectSelectedProjectId,
  selectIsConfigValid,
  selectedProjectHasCollection
} from '../../store/selectors';
import { SearchDetailConfig } from '../search-detail-config/SearchDetailConfig';
import { SearchInstanceConfig } from '../search-instance-config/SearchInstanceConfig';
import { messages } from './messages';

import './SearchOverlay.scss';
interface SearchOverlayProps {
  environmentId: string;
  skillId?: string;
  onClose: Noop;
}

export const SearchOverlay: React.FC<SearchOverlayProps> = ({
  environmentId,
  skillId,
  onClose
}) => {
  const { formatMessage: t } = useIntl();
  const dispatch = useAppDispatch();
  const draft = useDiscoveryDraft();

  const isUpdating = useAppSelector(selectIsUpdatingSkill);
  const isCreating = useAppSelector(selectIsCreatingSkill);
  const isConfigValid = useAppSelector(selectIsConfigValid);
  const isChangingProject = useAppSelector(selectIsChangingProject);
  const draftSkill = useAppSelector(selectDraftSearchSkill);
  const areProjectsLoading = useAppSelector(selectAreProjectsLoading);
  const selectedProjectId = useAppSelector(selectSelectedProjectId);
  const showDetailConfig = useAppSelector(isShowDetailConfig);
  const skill = useAppSelector(state => selectSkillById(state, skillId));
  const assistant = useAppSelector(selectAssistant);
  const skillReferences = useAppSelector(state =>
    selectSkillReferencesForEnvironment(state, environmentId)
  );
  const isConversationalSearchEnabled = useAppSelector(state =>
    isFeatureEnabled(state, FeatureFlag.DISCOVERY_LLM_CONVERSATIONAL_SEARCH)
  );
  const isInsightActionsEnabled = useAppSelector(selectIsInsightActionsEnabled);
  const isSapConversationalSearchEnabled = useAppSelector(state =>
    isFeatureEnabled(state, FeatureFlag.SAP_CONVERSATIONAL_SEARCH)
  );

  const projectHasCollection = useAppSelector(selectedProjectHasCollection);

  const areSkillsLoading = useAppSelector(selectAreSkillsLoading);
  // NOTE returns false if skillId null or search is older version
  const isAutocreated = useAppSelector(state =>
    selectIsSkillAutocreated(state, environmentId, skillId)
  );
  const isEnabled = useAppSelector(state =>
    selectIsSkillEnabledInEnvironment(state, environmentId, skillId)
  );

  useEffect(() => {
    if (isEnabled) {
      pageEvents.searchEdit(null, {
        skillId: skillId,
        waEnvironmentId: environmentId
      });
    } else {
      pageEvents.searchCreate(null, {
        waEnvironmentId: environmentId
      });
    }
  }, [environmentId, isEnabled, skillId]);

  useEffect(() => {
    if (skillId && !skill) {
      dispatch(listSkills(environmentId));
    }
  }, [skillId, skill, dispatch, environmentId]);

  useEffect(
    () => {
      if (isAutocreated) {
        // if working with autocreated searches, a search skill definition which includes the search_settings.discovery
        //  fields has already been configured once -- so set its details into the editing draft
        // if those search_settings.discovery fields are missing then this is a new-never-configured skill, so we
        //   use initializeAutocreatedSkill to make a new editing draft and copy the few details from the near-empty skill
        if (skill?.search_settings?.discovery) {
          if (
            isInsightActionsEnabled &&
            skill?.search_settings?.conversational_search?.search_confidence
              ?.threshold
          ) {
            delete skill.search_settings.conversational_search.search_confidence
              .threshold;
          }
          dispatch(setSearchSkillDraft({ skill, isDirty: false }));
        } else if (
          draft.isUninitialized ||
          skill?.skill_id !== draft?.skillId
        ) {
          // new autocreated search, initialize other fields
          draft.initializeAutocreatedSkill(skill);
        }

        // If this was previously an elastic search skill make sure to delete those settings from the draft.
        if (skill?.search_settings?.elastic_search) {
          dispatch(clearElasticDetails());
        }

        if (isConversationalSearchEnabled) {
          if (_.isEmpty(skill?.search_settings?.conversational_search)) {
            // If conversational search is enabled, and this is an older skill that doesn't have anything set for
            // conversational_search, make sure to set the values in draft to the defaults.
            draft.setConversationalSearchEnabledDefault();
            if (isInsightActionsEnabled) {
              draft.setConversationalSearchRetrievalConfidenceThresholdDefault();
              draft.setConversationalSearchResponseConfidenceThresholdDefault();
            } else {
              draft.setConversationalSearchConfidenceThresholdDefault();
            }
            draft.setConversationalSearchVerbosityOptionDefault();
            draft.setConversationalSearchMultiTurnDefault();
            if (!isSapConversationalSearchEnabled) {
              // If SAP conversational search is enabled we don't want to set a citations_title because they are using a
              // text response type instead of the new conversational_search response type. If SAP conversational search
              // is disabled then set a default for citations_title.
              draft.setCitationsTitleValueDefault();
            }
          } else if (
            !skill?.search_settings?.conversational_search?.citations_title &&
            !isSapConversationalSearchEnabled
          ) {
            // If there is no value for the citations title in the skill, and SAP conversational search is disabled, then
            // make sure to set the value in draft to the default.
            draft.setCitationsTitleValueDefault();
          }
        }

        // NB isNew = !skillId || !discoveryProjectId
        if (draft.isNew || isChangingProject) {
          dispatch(setShowDetailConfig(false));
        } else {
          dispatch(setShowDetailConfig(true));
        }
      }
      // if/when all Store deployments have moved to precreated-search then following `else` section can be deleted
      else {
        if (!!skill) {
          dispatch(setSearchSkillDraft({ skill: draftSkill, isDirty: false }));
        } else {
          if (
            !draft.isUninitialized &&
            (!selectedProjectId || selectedProjectId === '')
          ) {
            // no skill found, draft remaining from prior open so reset
            dispatch(setSearchSkillDraft({ skill: null, isDirty: false }));
          }
        }
        if (draft.isUninitialized || isChangingProject) {
          dispatch(setShowDetailConfig(false));
        } else {
          dispatch(setShowDetailConfig(true));
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- including all `draft` causes loop, need only isUninitialized & isNew
    [
      dispatch,
      draft.isUninitialized,
      draft.isNew,
      draft.skillId,
      isAutocreated,
      isEnabled,
      isChangingProject,
      selectedProjectId,
      skill,
      isConversationalSearchEnabled,
      isSapConversationalSearchEnabled
    ]
  );

  const handleClose = useCallback(() => {
    dispatch(setShowDetailConfig(false));
    dispatch(selectInstanceId(''));
    dispatch(selectProjectId(''));
    dispatch(setIsChangingProject(false));
    onClose();
  }, [dispatch, onClose]);

  const handleSubmit = useCallback(async () => {
    try {
      if (!showDetailConfig) {
        if (draft.isNew) {
          // save inst/proj iff autocreated (else store end has no record)
          if (isAutocreated) {
            draft.updateInstanceProjectIds();
          } else {
            draft.setNewDiscoveryDraft(environmentId);
          }
        } else {
          draft.updateInstanceProjectIds();
        }
        ctaEvents.searchNextConfigScreen(null, {
          waEnvironmentId: environmentId
        });
        dispatch(setShowDetailConfig(true));
      } else {
        if (!draft.isNew) {
          if (
            isInsightActionsEnabled &&
            draftSkill?.search_settings?.conversational_search
              ?.search_confidence?.threshold
          ) {
            delete draftSkill.search_settings.conversational_search
              .search_confidence.threshold;
          }

          // post the updates
          await dispatch(updateSkill(draftSkill)).unwrap();

          if (isAutocreated && !isEnabled) {
            // this is a disabled, precreated skill not yet enabled
            // send another post to set the skill_ref to enabled
            //  then segment event for CREATE
            const updatedSkillRefs = _.cloneDeep(skillReferences);
            const targetSkill = updatedSkillRefs.find(
              skillRef => skillRef.skill_id === skillId
            );
            targetSkill.disabled = false;
            const enablePayload = {
              assistant_id: environmentId,
              skill_references: updatedSkillRefs
            };
            if (assistant?.skill_references) {
              // FIXME: Move away from old way, see https://github.ibm.com/watson-engagement-advisor/wea-backlog/issues/56589
              await dispatch(updateAssistant(enablePayload));
            } else {
              await dispatch(
                updateEnvironment({
                  assistant_id: assistant?.assistant_id,
                  environment_id: environmentId,
                  skill_references: updatedSkillRefs
                })
              );
            }
            ctaEvents.searchCreate(null, { waEnvironmentId: environmentId });
          } else {
            // not an un-enabled autocreate (already enabled or older skill)
            // so no skill_ref enabling and segment as an UPDATE
            ctaEvents.searchUpdate(null, { waEnvironmentId: environmentId });
          }
        } else {
          // draft.isNew true for NEW and NOT PRE-CREATED
          // e.g. old-style search create (deprecated)
          await dispatch(createSkill(draftSkill)).unwrap();
          ctaEvents.searchCreate(null, { waEnvironmentId: environmentId });
        }
        handleClose();
      }
    } catch (error) {
      console.error(error);
    }
  }, [
    assistant,
    dispatch,
    draft,
    draftSkill,
    environmentId,
    handleClose,
    isAutocreated,
    isEnabled,
    showDetailConfig,
    skillId,
    skillReferences
  ]);

  let submitButtonLabel;
  if (!showDetailConfig) {
    submitButtonLabel = t(messages.next);
  } else if (skillId) {
    submitButtonLabel = t(messages.save);
  } else {
    submitButtonLabel = t(messages.create);
  }

  const isSearchSkillReadOnly = useAppSelector(state =>
    isSkillReadOnly(state, skillId)
  );
  const isAsstReadOnly = useAppSelector(state =>
    isAssistantReadOnly(state, assistant?.assistant_id)
  );
  const isEnvReadOnly = useAppSelector(state =>
    isEnvironmentReadOnly(state, environmentId)
  );

  // Skill.Write on an old (not autocreated) search skill is makes it writable
  // Need Env.Wr or Asst.Wr for autocreated or old
  const isReadOnly = isAutocreated
    ? isAsstReadOnly && isEnvReadOnly
    : isAsstReadOnly && isEnvReadOnly && isSearchSkillReadOnly;

  const isButtonEnabled =
    !isReadOnly &&
    ((!showDetailConfig &&
      selectedProjectId &&
      !areProjectsLoading &&
      projectHasCollection) ||
      (showDetailConfig && isConfigValid && (draft.isNew || draft.isDirty)));

  return (
    <Panel className="SearchOverlay" title={t(messages.title)}>
      <header
        className="SearchOverlay__header"
        aria-labelledby="integration-panel-heading">
        <div className="SearchOverlay__header--name">
          <h2 id="integration-panel-heading">{t(messages.title)}</h2>
        </div>
        <div className="ChannelOverlay__header--tag">
          <EnvironmentTag environmentId={environmentId} />
        </div>
        <div className="SearchOverlay__header--actions">
          <Button kind="secondary" onClick={handleClose} id="closePanel">
            {t(messages.close)}
          </Button>
          <Button
            kind="primary"
            onClick={handleSubmit}
            disabled={!isButtonEnabled}>
            {submitButtonLabel}
          </Button>
        </div>
      </header>
      <ReadOnlyNotification readOnly={isReadOnly} />
      <div className="SearchOverlay__content">
        {isUpdating || isCreating || areSkillsLoading ? (
          <Loading withOverlay />
        ) : showDetailConfig ? (
          <SearchDetailConfig draft={draft} readOnly={isReadOnly} />
        ) : (
          <SearchInstanceConfig waEnvironmentId={environmentId} />
        )}
      </div>
    </Panel>
  );
};

SearchOverlay.defaultProps = {
  skillId: undefined
};
